import React from "react";
import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import { useDropzone } from "react-dropzone";
import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_FILE_TYPES_GROUPED,
  MAX_FILE_SIZE_BYTES,
} from "@src/lib/utils/files";
import { cn } from "@src/lib/utils/cn";

type Props = {
  setMediaUrl: (url: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  isFileUploading: boolean;
  setIsFileUploading: (isUploading: boolean) => void;
};

const FileDropZone: React.FC<Props> = (props) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { handleFileUpload, isUploading: loading } = useS3Upload({
    onUploadComplete: (upload) => {
      props.setMediaUrl(upload.url);
      props.setIsFileUploading(false);
    },
  });

  const onDrop = React.useCallback(
    <T extends File>(acceptedFiles: T[]) => {
      if (acceptedFiles.length === 1) {
        handleFileUpload(acceptedFiles[0]);
      }
    },
    [handleFileUpload],
  );
  const { getRootProps } = useDropzone({
    useFsAccessApi: true,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE_BYTES,
    onDrop,
    accept: ACCEPTED_FILE_TYPES_GROUPED,
  });
  const maybeOpenFilePicker = React.useCallback(() => {
    if (props.onClick) {
      props.onClick();
    } else if (fileInputRef?.current && !props.onClick) {
      fileInputRef.current.click();
    }
  }, [fileInputRef, props.onClick]);
  React.useEffect(() => {
    if (!props.isFileUploading && loading) {
      props.setIsFileUploading(true);
    }
  }, [loading, props.isFileUploading, props]);
  return (
    <div className="flex flex-1 self-stretch" {...getRootProps()}>
      <div
        className={cn(
          "flex flex-1 flex-col items-center justify-center self-stretch",
          props.className,
        )}
        onClick={maybeOpenFilePicker}
      >
        <input
          type="file"
          multiple={false}
          ref={fileInputRef}
          disabled={props.disabled}
          style={{ display: "none" }}
          accept={ACCEPTED_FILE_TYPES.toString()}
          onChange={(e) => {
            if (e.target.files && e.target.files.length === 1) {
              handleFileUpload(e.target.files[0]);
            }
          }}
        />
        {props.children}
      </div>
    </div>
  );
};

export default FileDropZone;
